/**
 * @file src/offline/offlineSizes.js
 * @description How big a download will be, before any of it is fetched.
 *
 * A quality choice is only a real choice if the person can see what it costs, and a cap refusal is
 * only fair if it is right. Both need the byte length of objects nobody has downloaded yet.
 *
 * ## Why the size is asked for rather than calculated
 *
 * It is tempting to compute a WAV from its duration — 44.1 kHz, two bytes a sample — and the answer
 * is wrong in three ways at once. The canonical encode preserves the SOURCE channel count
 * (`spore/src/jobs/definitions/media/audioProcess.js` converts with `-ar 44100 -sample_fmt s16` and
 * no `-ac`), and nothing in the release payload says how many channels a track has, so mono is
 * over-counted by half and anything above stereo is under-counted. The container adds a header the
 * formula does not. And the compressed side is not derivable at all: a generated MP3 is 320 kbps,
 * but a release uploaded as a lossy file keeps its own object at whatever rate it arrived with.
 *
 * A refusal built on any of that would turn away downloads that fit. So the size is READ, from the
 * only thing that knows it: the object.
 *
 * ## One byte, per object, once
 *
 * `Range: bytes=0-0` against the audio proxy returns `206` with `Content-Range: bytes 0-0/<total>`
 * — the whole length, for one byte of body. The proxy already advertises `Accept-Ranges: bytes` and
 * forwards `Content-Range` from the object store (`mycelium/utils/spaces/objectStreamProxy.js`), so
 * this needs nothing new on the backend and works for releases that were published years ago.
 *
 * Answers are memoised for the session. A track's bytes at a given version and variant are immutable
 * — that is what the version in the key means — so asking twice is asking the same question.
 */
import { resolveApiBase } from '../api/httpClient.js';
import { getRoomGrantHeaders } from '../room/roomGrant.js';

/** `url` → byte length, or null for an object that would not say. Immutable per version+variant. */
const sizeCache = new Map();

function parseTotalFromContentRange(value) {
  // `bytes 0-0/12345678`, and `bytes 0-0/*` when the store does not know — which is not a number.
  const match = /\/(\d+)\s*$/.exec(String(value || ''));
  if (!match) return null;
  const total = Number.parseInt(match[1], 10);
  return Number.isFinite(total) && total > 0 ? total : null;
}

function isPlatformAudioUrl(url) {
  const base = resolveApiBase();
  if (!base || !url) return false;
  return String(url).startsWith(`${String(base).replace(/\/+$/, '')}/tracks/`);
}

/**
 * The exact byte length of one asset, or null when it cannot be established.
 *
 * Null is a real answer and callers must carry it rather than substitute a guess: a quote that
 * silently invents a number for the one track it could not measure is the same lie the arithmetic
 * was. What a caller does with an incomplete total is a decision it makes in the open.
 *
 * @param {string} url
 * @param {{ signal?: AbortSignal }} [options]
 * @returns {Promise<number|null>}
 */
export async function probeAssetBytes(url, { signal } = {}) {
  if (!url) return null;
  if (sizeCache.has(url)) return sizeCache.get(url);

  const platformAudio = isPlatformAudioUrl(url);
  let total = null;
  try {
    const response = await fetch(url, {
      signal,
      headers: {
        Range: 'bytes=0-0',
        ...(platformAudio ? getRoomGrantHeaders() : {}),
      },
      credentials: platformAudio ? 'include' : 'same-origin',
    });
    if (response.status === 206) {
      total = parseTotalFromContentRange(response.headers.get('Content-Range'));
    } else if (response.ok) {
      // A server that ignored the range answered with the whole object. Its `Content-Length` is
      // then the length we asked for, and the body is discarded unread.
      const declared = Number.parseInt(response.headers.get('Content-Length') || '', 10);
      total = Number.isFinite(declared) && declared > 0 ? declared : null;
    }
    // The body of a one-byte 206 is not worth reading; cancelling it releases the connection.
    response.body?.cancel?.().catch(() => {});
  } catch (error) {
    if (error?.name === 'AbortError') throw error;
    total = null;
  }

  // Only a real answer is remembered. Caching a failure would disable the pre-flight for that object
  // for the rest of the session, so a single blip on one track quietly turns the refusal off.
  if (total !== null) sizeCache.set(url, total);
  return total;
}

/**
 * Measure every audio asset a manifest names, in parallel but bounded.
 *
 * Bounded because a forty-track collection is forty requests and a browser will happily open all of
 * them: the point of a one-byte probe is that it is cheap, and forty simultaneous connections to the
 * same proxy is not cheap for the thing answering them.
 *
 * @param {object[]} assets `{ key, url }` shaped asset refs
 * @param {{ signal?: AbortSignal, concurrency?: number }} [options]
 * @returns {Promise<{ bytes: number, measured: number, unknown: number }>}
 */
export async function measureAssets(assets, { signal, concurrency = 6 } = {}) {
  const list = (assets || []).filter((item) => item?.url);
  let bytes = 0;
  let measured = 0;
  let unknown = 0;
  let cursor = 0;

  async function worker() {
    for (;;) {
      if (signal?.aborted) throw new DOMException('Cancelled', 'AbortError');
      const index = cursor;
      cursor += 1;
      if (index >= list.length) return;
      const size = await probeAssetBytes(list[index].url, { signal });
      if (size === null) unknown += 1;
      else {
        bytes += size;
        measured += 1;
      }
    }
  }

  await Promise.all(Array.from({ length: Math.min(concurrency, list.length) }, worker));
  return { bytes, measured, unknown };
}
